import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useTranslation } from 'react-i18next';
import { Apartment, DateRange, Bedtime } from '@mui/icons-material';
import { calculateNights } from '@frontend/features/common/helper';
import { HotelCancelPolicyIndicator } from '@frontend/features/hotel/components/HotelCancelPolicy';
import { HotelFareMetadataResponseDto } from '@bandido/shared/dtos';
import { HotelCancelPenalty } from '@frontend/features/hotel/components/HotelCancelPolicy/modal';

interface HotelDetailProps {
  net: number;
  markup?: string;
  total?: number;
  checkIn: string;
  checkOut: string;
  internalId: string;
  cancelPolicy: HotelFareMetadataResponseDto['cancelPolicy'];
  boardDescription: string;
  remarks: string;
  rateRules: string[];
}

export const SaleOrderHotelDetail = ({
  net,
  markup,
  total,
  checkIn,
  checkOut,
  cancelPolicy,
  boardDescription,
  remarks,
  rateRules
}: HotelDetailProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card variant="outlined">
      <CardContent
        sx={{
          background: theme.palette.paper.dark
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" gap={1} alignItems="center">
              <Apartment sx={{ color: theme.palette.text.secondary }}/>
              <Typography variant="subtitle1">{t('saleOrder.view.hotels.detail.title')}</Typography>
            </Box>
          </Box>
          <Divider/>
          <Grid container alignItems="center">
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.checkIn')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <DateRange fontSize="small" sx={{ color: theme.palette.text.secondary }}/>
                <Typography variant="body2">{checkIn}</Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.checkOut')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <DateRange fontSize="small" sx={{ color: theme.palette.text.secondary }}/>
                <Typography variant="body2">{checkOut}</Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.nights')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Bedtime fontSize="small" sx={{ color: theme.palette.text.secondary }}/>
                <Typography variant="body2">{calculateNights(checkIn, checkOut)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.subPrice')}
              </Typography>
              <Typography variant="body2">USD {net}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.markup')}
              </Typography>
              <Typography variant="body2">{markup}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.totalPrice')}
              </Typography>
              <Typography variant="body2">USD {total}</Typography>
            </Grid>
          </Grid>
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.board')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">{boardDescription}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.cancelPolicies')}
              </Typography>
              <HotelCancelPolicyIndicator
                refundable={cancelPolicy?.refundable}
                cancelPolicy={{
                  refundable: cancelPolicy?.refundable,
                  gross: cancelPolicy?.gross,
                  cancelPenalties: cancelPolicy?.cancelPenalties?.map((penalty: HotelCancelPenalty) => ({
                    deadline: penalty.deadline,
                    isCalculatedDeadline: penalty.isCalculatedDeadline,
                    penaltyType: penalty.penaltyType,
                    currency: penalty.currency,
                    value: penalty.value
                  })) || [],
                  description: cancelPolicy?.description
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography variant="body2">
                {t('saleOrder.view.hotels.detail.rateRules')}
              </Typography>
              {rateRules?.length ? rateRules?.map((rateRule) => {
                return (
                  <Chip
                    key={rateRule}
                    label={t(`hotels.fares.rateRules.values.${rateRule}`)}
                    size='small'
                  />
                )
              }) : t('common.notValueFound')}
            </Grid>
          </Grid>
          {
            remarks && (
              <Grid container alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="body2">
                    {t('saleOrder.view.hotels.detail.remarks')}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        '.ck-content': {
                          ...theme.typography.body2,
                          border: 'none'
                        },
                        '.ck-reset_all': {
                          display: 'none',
                        },
                        mt: theme.spacing(2)
                      }}
                    >
                      <CKEditor
                        editor={ClassicEditor as any}
                        data={remarks}
                        disabled
                        config={{
                          toolbar: [],
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )
          }
        </Box>
      </CardContent>
    </Card>
  );
};
